import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tidbitsAPI } from '../services/api';
import '../App.css';

function Tidbits() {
  const navigate = useNavigate();
  const [tidbits, setTidbits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, general, race-specific
  const [expandedTidbits, setExpandedTidbits] = useState({});

  // Separate pinned and unpinned tidbits
  const pinnedTidbits = tidbits.filter(t => t.isPinned);
  const unpinnedTidbits = tidbits.filter(t => !t.isPinned);

  useEffect(() => {
    loadTidbits();
  }, []);

  // Initialize all tidbits as expanded when data loads
  useEffect(() => {
    if (tidbits.length > 0) {
      const expanded = {};
      tidbits.forEach(tidbit => {
        expanded[tidbit.tidbitId] = true; // Default to expanded
      });
      setExpandedTidbits(expanded);
    }
  }, [tidbits]);

  const toggleExpand = (tidbitId) => {
    setExpandedTidbits(prev => ({
      ...prev,
      [tidbitId]: !prev[tidbitId]
    }));
  };

  const loadTidbits = async () => {
    try {
      setLoading(true);
      const response = await tidbitsAPI.getAllTidbits();
      
      if (response.success) {
        setTidbits(response.data.tidbits || []);
      }
    } catch (error) {
      console.error('Failed to load tidbits:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filter to unpinned tidbits only
  const filteredTidbits = unpinnedTidbits.filter(tidbit => {
    if (filter === 'general') {
      return !tidbit.raceId;
    } else if (filter === 'race-specific') {
      return tidbit.raceId;
    }
    return true; // 'all'
  });

  if (loading) {
    return <div className="loading">Loading tidbits...</div>;
  }

  return (
    <div className="tidbits-page">
      <h2>Tidbits & Insights</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Expert insights, horse information, and racing tips from our team
      </p>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setFilter('all')}
            className="submit-button"
            style={{
              flex: '0 1 auto',
              background: filter === 'all' ? '#2196F3' : '#f5f5f5',
              color: filter === 'all' ? 'white' : '#333',
              border: '1px solid #ddd'
            }}
          >
            All Tidbits ({tidbits.length})
          </button>
          <button
            onClick={() => setFilter('general')}
            className="submit-button"
            style={{
              flex: '0 1 auto',
              background: filter === 'general' ? '#2196F3' : '#f5f5f5',
              color: filter === 'general' ? 'white' : '#333',
              border: '1px solid #ddd'
            }}
          >
            General ({tidbits.filter(t => !t.raceId).length})
          </button>
          <button
            onClick={() => setFilter('race-specific')}
            className="submit-button"
            style={{
              flex: '0 1 auto',
              background: filter === 'race-specific' ? '#2196F3' : '#f5f5f5',
              color: filter === 'race-specific' ? 'white' : '#333',
              border: '1px solid #ddd'
            }}
          >
            Race-Specific ({tidbits.filter(t => t.raceId).length})
          </button>
        </div>
      </div>

      {/* Pinned Tidbits */}
      {pinnedTidbits.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            📌 Pinned Announcements
          </h3>
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {pinnedTidbits.map(tidbit => (
              <div
                key={tidbit.tidbitId}
                style={{
                  background: 'linear-gradient(98deg, #031155 0%, #8d4ba2f7 100%)',
                  color: 'white',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                }}
              >
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>📌</span>
                      <h3 style={{ margin: 0, color: 'white' }}>{tidbit.title}</h3>
                    </div>
                    <button
                      onClick={() => toggleExpand(tidbit.tidbitId)}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '0.35rem 0.7rem',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        flex: '0 0 auto',
                        whiteSpace: 'nowrap',
                        minWidth: '0',
                        width: 'auto',
                        marginLeft: '0.5rem'
                      }}
                    >
                      {expandedTidbits[tidbit.tidbitId] ? '▼ Collapse' : '▶ Expand'}
                    </button>
                  </div>
                  
                  {tidbit.horseName && tidbit.raceName && (
                    <div
                      style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        color: 'white',
                        marginTop: '0.5rem',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/races/${tidbit.raceId}`)}
                    >
                      🏇 {tidbit.horseName} - {tidbit.raceName}
                    </div>
                  )}
                  
                  {tidbit.raceName && !tidbit.horseName && (
                    <div
                      style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        color: 'white',
                        marginTop: '0.5rem',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/races/${tidbit.raceId}`)}
                    >
                      🏁 {tidbit.raceName}
                    </div>
                  )}
                </div>

                {expandedTidbits[tidbit.tidbitId] && (
                  <>
                    <div
                      className="tidbit-content"
                      style={{
                        fontSize: '1rem',
                        lineHeight: '1.6',
                        color: 'rgba(255,255,255,0.95)',
                        marginBottom: '1rem'
                      }}
                      dangerouslySetInnerHTML={{ __html: tidbit.content }}
                    />

                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '0.75rem' }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>
                        By {tidbit.author} • {new Date(tidbit.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regular Tidbits */}
      {pinnedTidbits.length > 0 && filteredTidbits.length > 0 && (
        <h3 style={{ marginBottom: '1rem' }}>All Tidbits</h3>
      )}

      {filteredTidbits.length === 0 && pinnedTidbits.length === 0 ? (
        pinnedTidbits.length === 0 ? (
          <div className="card">
            <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
              {filter === 'all' 
                ? 'No tidbits available yet. Check back soon!'
                : `No ${filter === 'general' ? 'general' : 'race-specific'} tidbits available.`
              }
            </p>
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
            {filter === 'all' 
              ? null
              : `No ${filter === 'general' ? 'general' : 'race-specific'} tidbits in this category.`
            }
          </p>
        )
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {filteredTidbits.map(tidbit => (
            <div key={tidbit.tidbitId} className="card">
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>{tidbit.title}</h3>
                  <button
                    onClick={() => toggleExpand(tidbit.tidbitId)}
                    style={{
                      padding: '0.35rem 0.7rem',
                      fontSize: '0.85rem',
                      flex: '0 0 auto',
                      whiteSpace: 'nowrap',
                      minWidth: '0',
                      width: 'auto',
                      marginLeft: '0.5rem',
                      background: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {expandedTidbits[tidbit.tidbitId] ? '▼ Collapse' : '▶ Expand'}
                  </button>
                </div>
                
                {tidbit.horseName && tidbit.raceName && (
                  <div
                    style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      background: '#e3f2fd',
                      borderRadius: '12px',
                      fontSize: '0.9rem',
                      color: '#1976d2',
                      marginBottom: '0.5rem',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/races/${tidbit.raceId}`)}
                  >
                    🏇 {tidbit.horseName} - {tidbit.raceName}
                  </div>
                )}
                
                {tidbit.raceName && !tidbit.horseName && (
                  <div
                    style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      background: '#f3e5f5',
                      borderRadius: '12px',
                      fontSize: '0.9rem',
                      color: '#7b1fa2',
                      marginBottom: '0.5rem',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/races/${tidbit.raceId}`)}
                  >
                    🏁 {tidbit.raceName}
                  </div>
                )}
                
                {!tidbit.raceName && (
                  <div
                    style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      background: '#fff3e0',
                      borderRadius: '12px',
                      fontSize: '0.9rem',
                      color: '#e65100'
                    }}
                  >
                    📝 General
                  </div>
                )}
              </div>

              {expandedTidbits[tidbit.tidbitId] && (
                <>
                  <div
                    className="tidbit-content"
                    style={{
                      fontSize: '1rem',
                      lineHeight: '1.6',
                      color: '#333',
                      marginBottom: '1rem'
                    }}
                    dangerouslySetInnerHTML={{ __html: tidbit.content }}
                  />

                  <div style={{ borderTop: '1px solid #eee', paddingTop: '0.75rem' }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#999' }}>
                      By {tidbit.author} • {new Date(tidbit.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Tidbits;
